import { caseworkNew } from "building-generation/elements/caseworks/casework";
import type { Casework } from "building-types/casework/caseworkTypes";
import { CaseworkTypeName } from "building-types/casework/caseworkTypesEnum";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import { WallType } from "building-types/walls/wallEnums";
import { ensureDefined } from "common/utils/assertOfType";
import type { Extrusion } from "geom/brep";
import { extrudePolyLine2InZ, extrudeSquare } from "geom/extrude";
import { polyLine2EdgeLength } from "geom/polyLine2";
import { polyLine2New } from "geom/polyLine2Types";
import { rangeInclusiveByMinStep } from "geom/range";
import { makeRectFromLine } from "geom/rect2";
import { vec2Lerp } from "geom/vec2";
import { WallStripImpl } from "../walls/wallStripImpl";
import type { RoomLayoutGenerator } from "./roomLayout";

const roomLayoutUtility: RoomLayoutGenerator[] = [
  {
    roomType: RoomType.OPEN_BELOW,
    generate({ room, wallIndex }) {
      const rwalls = wallIndex.wallsInRoom(room);

      const voidWalls = rwalls.filter((wall) => wall.wallType === WallType.VOID);

      voidWalls.sort((a, b) => b.path.length - a.path.length);

      const rails: Extrusion[] = [];

      const railHeight = 36;
      const railSillHeight = 4;
      const railThick = 1;
      const balDist = 4;
      const balThick = 1;

      const casework: Casework[] = [];

      for (const host of voidWalls) {
        if (ensureDefined(host.path.leftRoom).type !== RoomType.GENERAL_OPEN) {
          continue;
        }
        const wall = polyLine2New([host.path.startX, host.path.startY, host.path.endX, host.path.endY]);

        const edgeDist = polyLine2EdgeLength(wall, 0);
        const railPl = makeRectFromLine(wall, 1, 1);
        rails.push(extrudePolyLine2InZ(railPl, room.level.floorElevation + railHeight, railThick));
        rails.push(extrudePolyLine2InZ(railPl, room.level.floorElevation + railSillHeight, railThick));

        const divs = rangeInclusiveByMinStep(0, edgeDist, balDist);
        for (const div of divs) {
          const norm = div / edgeDist;
          const [x, y] = vec2Lerp(wall[0], wall[1], wall[2], wall[3], norm);
          rails.push(
            extrudeSquare(
              x,
              y,
              room.level.floorElevation + railSillHeight + railThick,
              railHeight - railThick - railSillHeight,
              balThick,
            ),
          );
        }
        casework.push(caseworkNew({ room, decorativeExtrusions: rails, name: CaseworkTypeName.RAILING }));
      }

      return { casework };
    },
  },
  {
    roomType: RoomType.CHASE,
    generate({ room, wallIndex }) {
      const casework: Casework[] = [];
      if (room.outline.length === 10) {
        const walls = wallIndex.wallsInRoom(room);
        const wallStrip = new WallStripImpl(room, walls, 0, walls.length);

        const roomOutline = wallStrip.baseline(true);

        if (roomOutline.length === 10) {
          const cross1 = polyLine2New([roomOutline[0], roomOutline[1], roomOutline[4], roomOutline[5]]);
          const cross2 = polyLine2New([roomOutline[2], roomOutline[3], roomOutline[6], roomOutline[7]]);

          const planSolid = [cross1, cross2];

          const chaseCross = caseworkNew({ room, planSolid, name: CaseworkTypeName.CHASE });
          casework.push(chaseCross);
        }
      }

      return { casework };
    },
  },
];

export default roomLayoutUtility;
